import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { DialogPlugin, Link, MessagePlugin } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { unloadNamespace, updateNamespace } from '@/services/kopNamespaceService';
import Bundles from '@/components/Cluster/Bundles';
import Tip from '@/components/Tip';
import Descriptions from '@/components/Descriptions';
import EllipsisTip from '@/components/EllipsisTip';
import usePerms from '@/hooks/usePerms';
import { getTopicDataList } from '@/services/kopBrokerService';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';

function BasicPanel({ commonProps, overview, onRefresh, deployEnv }) {
  const [hasUninstall] = usePerms(['kop:namespace:list:uninstall']);
  const confirmBtn = useDisableButton({ content: '确定' });

  const onNoteChange = async (note) => {
    const params = {
      ...commonProps,
      note,
    };
    try {
      await updateNamespace(params);
      MessagePlugin.success('修改成功！');
      onRefresh();
    } catch (error) {
      return false;
    }
  };

  const doUnload = useMemoizedFn((bundle) => {
    const dialog = DialogPlugin.confirm({
      header: '手动卸载',
      theme: 'warning',
      body: '确定卸载？',
      confirmBtn,
      onClose: () => dialog.hide(),
      onConfirm: async () => {
        try {
          dialog.update({
            confirmBtn: { loading: true, content: '确定' },
          });
          await unloadNamespace({ ...commonProps, bundle });
          MessagePlugin.success('操作成功！');
          dialog.hide(); // 关闭弹窗
          onRefresh();
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          dialog.update({
            confirmBtn: { loading: false, content: '确定' },
          });
        }
      },
    });
  });

  const extraColumns = useMemo(() => {
    if (hasUninstall) {
      return [
        {
          colKey: 'option',
          title: '操作',
          fixed: 'right',
          align: 'center',
          width: 200,
          cell: ({ row }) => (
            <Link theme='primary' hover='color' onClick={() => doUnload(row.bundleRange)}>
              手动卸载
            </Link>
          ),
        },
      ];
    }
    return [];
  }, [hasUninstall, doUnload]);

  return (
    <Root>
      <Descriptions title='基本信息'>
        <Descriptions.Item label='命名空间' value={overview.namespace} />
        <Descriptions.Item label='集群名称' value={overview.clusterName} />
        <Descriptions.Item label='创建时间' value={overview.createTime} type='datetime' />
        <Descriptions.Item label='所属应用' value={overview.app} />
        {overview.note && ( // 业务逻辑
          <Descriptions.Item
            label='备注'
            value={overview.note}
            render={(val) => <EllipsisTip>{val}</EllipsisTip>}
            editable
            onChange={onNoteChange}
          />
        )}
      </Descriptions>
      {deployEnv !== 'container' && (
        <>
          <H3
            style={{ margin: '-10px 0 10px' }}
            content='单个Broker分配的Bundle个数（可进行负载均衡最小粒度）'
          >
            Bundles
          </H3>
          <Bundles
            extraColumns={extraColumns}
            originData={overview.bundleStatsList || []}
            getListRequest={getTopicDataList}
          />
        </>
      )}
    </Root>
  );
}

export default connect((state) => ({
  overview: state.kopNamespace.detailOverview,
  deployEnv: state.kopCluster.detail.deployEnv,
}))(BasicPanel);

const Root = styled.div`
  padding: 16px 6px 0;
`;

const H3 = styled(Tip)`
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: var(--td-font-gray-1);
  padding: 20px 0 6px;
  display: flex;
  align-items: center;
`;
